/**
 * vaultService — API client for CorpBrain's Vault (internal Drive).
 *
 * Base URL is already `<origin>/api/v1`, so paths here start at `/vault`.
 * Auth (Bearer + X-Tenant-ID) is auto-attached by the axios interceptor.
 * Mirrors the 15 backend routes in backend/modules/vault/api/routes.py.
 */

import api from '@services/api';
import type { ShareableTenant, ShareableUser } from '@/types/admin';
import type { VaultFolder, VaultFile, VaultAccess, VaultAudience, VaultShare, VaultAgentLink, VaultFileContent, AccessMap, RenameImpact, MoveImpact, FolderDeleteImpact, FileDeleteImpact, VaultOrgSettings, SharedFileEntry, VaultLimits, DriveCopyResult, BulkItem, BulkCheckItem, BulkMoveImpact, BulkDeleteImpact, BulkOutcome } from './types';

export interface CreateSharePayload {
  folder_id?: string;
  file_id?: string;
  shared_with_user_id?: string;
  shared_with_tenant_id?: string;
  can_edit?: boolean;
  can_delete?: boolean;
  can_reshare?: boolean;
  /** Tenant grantees only. true places the node on that organization's shelf
   *  (their admins take control); false is a quiet share — members get the
   *  checkbox rights in their own "Shared with me". Backend defaults TRUE,
   *  so the drawer always sends it explicitly for tenant grantees. */
  to_shelf?: boolean;
}

export interface CreateAgentLinkPayload {
  folder_id?: string;
  file_id?: string;
  chatbot_id?: string;
  skill_id?: string;
  subject_tenant_id?: string;
  access_mode?: 'read' | 'edit';
  include_as_context?: boolean;
  display_name?: string;
}

export const vaultService = {
  // ── Folder tree ────────────────────────────────────────────────
  listRootFolders: async (): Promise<VaultFolder[]> => {
    const res = await api.get<{ folders: VaultFolder[] }>('/vault/folders');
    return res.data.folders;
  },

  listSharedFiles: async (): Promise<SharedFileEntry[]> => {
    const res = await api.get<{ files: SharedFileEntry[] }>('/vault/shared-files');
    return res.data.files;
  },

  getTree: async (): Promise<VaultFolder[]> => {
    const res = await api.get<{ tree: VaultFolder[] }>('/vault/tree');
    return res.data.tree;
  },

  getFolder: async (folderId: string): Promise<VaultFolder> => {
    const res = await api.get<{ folder: VaultFolder }>(`/vault/folders/${folderId}`);
    return res.data.folder;
  },

  createFolder: async (folderName: string, parentFolderId?: string): Promise<VaultFolder> => {
    const res = await api.post<{ folder: VaultFolder }>('/vault/folders', {
      folder_name: folderName,
      parent_folder_id: parentFolderId ?? null,
    });
    return res.data.folder;
  },

  renameFolder: async (folderId: string, folderName: string): Promise<VaultFolder> => {
    const res = await api.patch<{ folder: VaultFolder }>(`/vault/folders/${folderId}`, {
      folder_name: folderName,
    });
    return res.data.folder;
  },

  moveFolder: async (folderId: string, parentFolderId: string | null): Promise<VaultFolder> => {
    const res = await api.patch<{ folder: VaultFolder }>(`/vault/folders/${folderId}`, {
      parent_folder_id: parentFolderId,
    });
    return res.data.folder;
  },

  deleteFolder: async (folderId: string): Promise<void> => {
    await api.delete(`/vault/folders/${folderId}`);
  },

  saveFolder: async (folderId: string): Promise<{ files: number; summaries_refreshed: number }> => {
    const res = await api.post<{ result: { files: number; summaries_refreshed: number } }>(
      `/vault/folders/${folderId}/save`,
    );
    return res.data.result;
  },

  // ── Files ──────────────────────────────────────────────────────
  /** What the Library will accept. Fetched once so the browser can refuse a
   *  hopeless upload without spending minutes sending it first; the server
   *  stays the authority and checks again. */
  limits: async (): Promise<VaultLimits> => {
    const { data } = await api.get('/vault/limits');
    return data;
  },

  uploadToFolder: async (
    folderId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ placement: VaultFile; outcome: 'added' | 'replaced' | 'unchanged' }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<{ placement: VaultFile; outcome: 'added' | 'replaced' | 'unchanged' }>(
      `/vault/folders/${folderId}/upload`,
      form,
      {
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress((e.loaded / e.total) * 100);
        },
      },
    );
    return { placement: res.data.placement, outcome: res.data.outcome || 'added' };
  },

  /** Copy files from the person's Google Drive into a folder — a stationary
   *  copy, exactly as if they had downloaded and uploaded them. One answer per
   *  pick (207), so a refused file never fails the ones beside it; no
   *  connection at all is a 409 CLOUD_NOT_CONNECTED for the whole call. */
  uploadFromDrive: async (folderId: string, driveFileIds: string[]): Promise<DriveCopyResult[]> => {
    const res = await api.post<{ results: DriveCopyResult[] }>(
      `/vault/folders/${folderId}/upload-from-drive`,
      { drive_file_ids: driveFileIds },
    );
    return res.data.results;
  },

  removeFile: async (folderId: string, fileId: string): Promise<void> => {
    await api.delete(`/vault/folders/${folderId}/files/${fileId}`);
  },

  // ── Move / copy / impact previews ──────────────────────────────
  moveFile: async (
    folderId: string, fileId: string, newFolderId: string,
  ): Promise<VaultFile> => {
    const res = await api.post<{ placement: VaultFile }>(
      `/vault/folders/${folderId}/files/${fileId}/move`,
      { new_folder_id: newFolderId },
    );
    return res.data.placement;
  },

  /** Place the SAME file into another folder — a second doorway to the one
   *  real file, never a fork. Whoever reaches the destination folder gets
   *  their folder rights on the real file; an edit anywhere changes it
   *  everywhere. as_draft:false — placing an already-indexed file must not
   *  demote it back to draft. */
  placeFile: async (
    destFolderId: string, fileId: string, displayName: string,
  ): Promise<VaultFile> => {
    const res = await api.post<{ placement: VaultFile }>(
      `/vault/folders/${destFolderId}/files`,
      { file_id: fileId, file_display_name: displayName, as_draft: false },
    );
    return res.data.placement;
  },

  copyFile: async (
    folderId: string, fileId: string,
    opts?: { dest_folder_id?: string; new_display_name?: string },
  ): Promise<VaultFile> => {
    const res = await api.post<{ placement: VaultFile }>(
      `/vault/folders/${folderId}/files/${fileId}/copy`, opts || {},
    );
    return res.data.placement;
  },

  /** Folder move preview (no file_id) or file-placement move preview. */
  moveImpact: async (params: {
    folder_id: string; file_id?: string; new_parent_id?: string | null;
  }): Promise<MoveImpact> => {
    const res = await api.get<{ impact: MoveImpact }>('/vault/move-impact', {
      params: {
        folder_id: params.folder_id,
        ...(params.file_id ? { file_id: params.file_id } : {}),
        ...(params.new_parent_id ? { new_parent_id: params.new_parent_id } : {}),
      },
    });
    return res.data.impact;
  },

  folderDeleteImpact: async (folderId: string): Promise<FolderDeleteImpact> => {
    const res = await api.get<{ impact: FolderDeleteImpact }>(
      `/vault/folders/${folderId}/delete-impact`,
    );
    return res.data.impact;
  },

  fileDeleteImpact: async (folderId: string, fileId: string): Promise<FileDeleteImpact> => {
    const res = await api.get<{ impact: FileDeleteImpact }>(
      `/vault/folders/${folderId}/files/${fileId}/delete-impact`,
    );
    return res.data.impact;
  },

  // ── The organization's charter ─────────────────────────────────
  getOrgSettings: async (): Promise<VaultOrgSettings> => {
    const res = await api.get<{ settings: VaultOrgSettings }>('/vault/org-settings');
    return res.data.settings;
  },

  setMembersCanAdd: async (enabled: boolean): Promise<VaultOrgSettings> => {
    const res = await api.patch<{ settings: VaultOrgSettings }>('/vault/org-settings', {
      members_can_add: enabled,
    });
    return res.data.settings;
  },

  getFileContent: async (fileId: string): Promise<VaultFileContent> => {
    const res = await api.get<{ file: VaultFileContent }>(`/vault/files/${fileId}/content`);
    return res.data.file;
  },

  putFileContent: async (fileId: string, content: string): Promise<void> => {
    await api.put(`/vault/files/${fileId}/content`, { content });
  },

  /** Replace THIS file's content with a newer version edited outside the app.
   *  The file keeps its id, so every placement, agent link and share it already
   *  had still points at it — it is the same document, newer. Must be the same
   *  file type; the server refuses a mismatch by name. */
  replaceFileContent: async (
    fileId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<{ bytes: number; chunks_rebuilt: number }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<{ result: { bytes: number; chunks_rebuilt: number } }>(
      `/vault/files/${fileId}/replace`,
      form,
      {
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      },
    );
    return res.data.result;
  },

  updateFileSettings: async (
    fileId: string,
    settings: { management_mode?: 'managed' | 'locked'; edit_approval_mode?: 'ask' | 'auto' | 'never' },
  ): Promise<void> => {
    await api.patch(`/vault/files/${fileId}/settings`, settings);
  },

  // ── People access (shares) ─────────────────────────────────────
  getAccess: async (node: { folder_id?: string; file_id?: string }): Promise<VaultAccess> => {
    const res = await api.get<{ access: VaultAccess }>('/vault/access', { params: node });
    return res.data.access;
  },

  /**
   * Start a new, empty file of a chosen kind inside a folder.
   *
   * The third way in, beside uploading and fetching from a URL — and the only
   * one that does not assume the document already exists somewhere else.
   */
  createBlankFile: async (
    folderId: string,
    kind: 'document' | 'sheet' | 'deck' | 'markdown' | 'text' | 'csv',
    name?: string,
  ): Promise<VaultFile> => {
    const res = await api.post<{ placement: VaultFile }>(
      `/vault/folders/${folderId}/new-file`, { kind, ...(name ? { name } : {}) },
    );
    return res.data.placement;
  },

  /** Find a folder or a file by name, anywhere the caller can see. */
  search: async (q: string): Promise<{
    folders: { folder_id: string; folder_name: string; tenant_name?: string | null }[];
    files: { file_id: string; folder_id: string; file_display_name: string; folder_name?: string | null }[];
    capped?: boolean;
  }> => {
    // In the body, never the URL: the platform's request log keeps every URL.
    const res = await api.post('/vault/search', { q });
    return { folders: res.data.folders || [], files: res.data.files || [], capped: res.data.capped };
  },

  listShares: async (node: { folder_id?: string; file_id?: string }): Promise<VaultShare[]> => {
    const res = await api.get<{ shares: VaultShare[] }>('/vault/shares', { params: node });
    return res.data.shares;
  },

  /**
   * Who this person may share THIS node with.
   *
   * Vault-owned on purpose: the right to share comes from the node, not from an
   * admin role, so the list has to be gated the same way. The group-admin
   * endpoint this used to call returns 403 to everyone else, which left a
   * re-sharer looking at an empty picker.
   */
  getShareTargets: async (
    node: { folder_id?: string; file_id?: string },
  ): Promise<{ users: ShareableUser[]; tenants: ShareableTenant[] }> => {
    const res = await api.get<{ users: ShareableUser[]; tenants: ShareableTenant[] }>(
      '/vault/share-targets', { params: node },
    );
    return { users: res.data.users, tenants: res.data.tenants };
  },

  createShare: async (payload: CreateSharePayload): Promise<VaultShare> => {
    const res = await api.post<{ share: VaultShare }>('/vault/shares', payload);
    return res.data.share;
  },

  revokeShare: async (shareId: string): Promise<void> => {
    await api.delete(`/vault/shares/${shareId}`);
  },

  /**
   * Who can OPEN this node, as people rather than as rows.
   *
   * `listShares` answers with grant rows, which is the wrong unit for a panel a
   * person reads: a folder shared with an organization is one row, and offering
   * to share a file inside it with someone already in that organization is
   * offering to do nothing at all.
   */
  getAudience: async (node: { folder_id?: string; file_id?: string }): Promise<VaultAudience> => {
    const res = await api.get<VaultAudience>('/vault/audience', { params: node });
    return {
      organizations: res.data.organizations || [],
      people: res.data.people || [],
      managers: res.data.managers || [],
    };
  },

  /** Write a grant's rights EXACTLY — the only path that can lower one. */
  setShareRights: async (
    shareId: string,
    rights: { can_edit: boolean; can_delete: boolean; can_reshare: boolean },
  ): Promise<VaultShare> => {
    const res = await api.patch<{ share: VaultShare }>(`/vault/shares/${shareId}`, rights);
    return res.data.share;
  },

  // ── Agent access (links) ───────────────────────────────────────
  usedBy: async (node: { folder_id?: string; file_id?: string }): Promise<VaultAgentLink[]> => {
    const res = await api.get<{ used_by: VaultAgentLink[] }>('/vault/used-by', { params: node });
    return res.data.used_by;
  },

  listAgentLinks: async (subject: {
    chatbot_id?: string;
    skill_id?: string;
    subject_tenant_id?: string;
  }): Promise<VaultAgentLink[]> => {
    const res = await api.get<{ links: VaultAgentLink[] }>('/vault/agent-links', { params: subject });
    return res.data.links;
  },

  createAgentLink: async (payload: CreateAgentLinkPayload): Promise<VaultAgentLink> => {
    const res = await api.post<{ link: VaultAgentLink }>('/vault/agent-links', payload);
    return res.data.link;
  },

  updateAgentLink: async (
    linkId: string,
    payload: { access_mode?: 'read' | 'edit'; include_as_context?: boolean },
  ): Promise<VaultAgentLink> => {
    const res = await api.patch<{ link: VaultAgentLink }>(`/vault/agent-links/${linkId}`, payload);
    return res.data.link;
  },

  deleteAgentLink: async (linkId: string): Promise<void> => {
    await api.delete(`/vault/agent-links/${linkId}`);
  },

  /** What would notice if this folder or file were renamed — asked BEFORE. */
  renameImpact: async (
    node: { folder_id?: string; file_id?: string },
  ): Promise<RenameImpact> => {
    const res = await api.get<{ impact: RenameImpact }>('/vault/rename-impact', { params: node });
    return res.data.impact;
  },

  renameFile: async (
    folderId: string, fileId: string, fileDisplayName: string,
  ): Promise<void> => {
    await api.patch(`/vault/folders/${folderId}/files/${fileId}`, {
      file_display_name: fileDisplayName,
    });
  },

  /**
   * Save a file, or a whole folder as a .zip.
   *
   * `responseType: 'blob'` is what makes these work: the axios interceptor
   * attaches the Bearer token and X-Tenant-ID, which a plain <a href> to the
   * same URL could not do — it would arrive unauthenticated and be refused.
   */
  downloadFile: async (fileId: string): Promise<Blob> => {
    const res = await api.get(`/vault/files/${fileId}/download`,
                              { responseType: 'blob' });
    return res.data as Blob;
  },

  downloadFolder: async (folderId: string): Promise<Blob> => {
    const res = await api.get(`/vault/folders/${folderId}/download`,
                              { responseType: 'blob' });
    return res.data as Blob;
  },

  // ── Multi-select: one operation on several items ───────────────
  /** What each selected item allows, per action — the backend's rules,
   *  asked so the bar can disable an action and say why. */
  bulkCheck: async (items: BulkItem[]): Promise<BulkCheckItem[]> => {
    const res = await api.post<{ items: BulkCheckItem[] }>('/vault/bulk/check', { items });
    return res.data.items;
  },

  bulkMoveImpact: async (items: BulkItem[], destFolderId: string): Promise<BulkMoveImpact> => {
    const res = await api.post<{ impact: BulkMoveImpact }>('/vault/bulk/impact', {
      action: 'move', items, dest_folder_id: destFolderId,
    });
    return res.data.impact;
  },

  bulkDeleteImpact: async (items: BulkItem[]): Promise<BulkDeleteImpact> => {
    const res = await api.post<{ impact: BulkDeleteImpact }>('/vault/bulk/impact', {
      action: 'delete', items,
    });
    return res.data.impact;
  },

  /* The writes answer 207 — one result per item, so a refused item never
     fails the ones beside it. */
  bulkMove: async (items: BulkItem[], destFolderId: string): Promise<BulkOutcome> => {
    const res = await api.post<BulkOutcome>('/vault/bulk/move', {
      items, dest_folder_id: destFolderId,
    });
    return res.data;
  },

  /** `namePattern` carries `{name}` — the localized "Copy of {name}". */
  bulkCopy: async (
    items: BulkItem[], destFolderId: string, namePattern: string,
  ): Promise<BulkOutcome> => {
    const res = await api.post<BulkOutcome>('/vault/bulk/copy', {
      items, dest_folder_id: destFolderId, name_pattern: namePattern,
    });
    return res.data;
  },

  bulkDelete: async (items: BulkItem[]): Promise<BulkOutcome> => {
    const res = await api.post<BulkOutcome>('/vault/bulk/delete', { items });
    return res.data;
  },

  bulkShare: async (
    items: BulkItem[],
    grantees: ({ user_id: string } | { tenant_id: string })[],
    rights: { can_edit: boolean; can_delete: boolean; can_reshare: boolean },
    toShelf: boolean,
  ): Promise<BulkOutcome> => {
    const res = await api.post<BulkOutcome>('/vault/bulk/share', {
      items, grantees, ...rights, to_shelf: toShelf,
    });
    return res.data;
  },

  bulkDownload: async (items: BulkItem[], zipName: string): Promise<Blob> => {
    const res = await api.post('/vault/bulk/download', { items, zip_name: zipName },
                               { responseType: 'blob' });
    return res.data as Blob;
  },

  // ── The same two systems, read from the subject's end ──────────
  getAccessMap: async (params: {
    tenant_id?: string;
    chatbot_id?: string;
    skill_id?: string;
    subject_tenant_id?: string;
    user_id?: string;
  } = {}): Promise<AccessMap> => {
    const res = await api.get<{ access_map: AccessMap }>('/vault/access-map', { params });
    return res.data.access_map;
  },
};
